import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit,
  Delete,
  Check,
  Close,
} from "@mui/icons-material";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { deleteData } from "../../Services/deleteData";
import { endpoints } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { IDocumentTypeManagerModalProps } from "../../Utils/Types";
import useCustomMemo from "../../Hooks/useCustomMemo";

export default function DocumentTypeManagerModal({
  open,
  onClose,
  documentTypes,
  onRefresh,
}: IDocumentTypeManagerModalProps) {
  const memo = useCustomMemo();
  const orderTypes = memo?.storage?.orderTypes || [];

  const getOrderTypeIds = (value: any[]): string[] => {
    if (!Array.isArray(value)) return [];
    return value.map((ot) => (typeof ot === "string" ? ot : ot?._id || "")).filter(Boolean);
  };

  const [formState, setFormState] = useState({
    newType: {
      name: "",
      slug: "",
      isTemplate: false,
      orderTypeIds: [] as string[],
    },
    editType: {
      data: null as any,
      name: "",
      slug: "",
      isTemplate: false,
      orderTypeIds: [] as string[],
    },
    showAddForm: false,
  });

  useEffect(() => {
    if (open) {
      setFormState({
        newType: {
          name: "",
          slug: "",
          isTemplate: false,
          orderTypeIds: [],
        },
        editType: {
          data: null,
          name: "",
          slug: "",
          isTemplate: false,
          orderTypeIds: [],
        },
        showAddForm: false,
      });
    }
  }, [open]);

  const handleEditType = (type: any) => {
    setFormState((prev) => ({
      ...prev,
      editType: {
        data: type,
        name: type.name,
        slug: type.slug || "",
        isTemplate: type.isTemplate || false,
        orderTypeIds: getOrderTypeIds(type.orderTypeIds),
      },
    }));
  };

  const handleUpdateType = async () => {
    if (!formState.editType.name.trim() || !formState.editType.data) return;

    try {
      const payload: any = {
        name: formState.editType.name.trim(),
        slug: formState.editType.slug.trim(),
        isTemplate: formState.editType.isTemplate,
        orderTypeIds: formState.editType.orderTypeIds,
      };

      await updateData(
        `${endpoints.documentTypes}/${formState.editType.data._id}`,
        payload
      );
      onRefresh();
      cancelEdit();
    } catch (error) {
      console.error("Error updating document type:", error);
    }
  };

  const handleDeleteType = async (typeId: string) => {
    const typeToDelete = documentTypes.find((t) => t._id === typeId);

    if (typeToDelete?.slug) {
      return;
    }

    try {
      await deleteData(`${endpoints.documentTypes}/${typeId}`);
      onRefresh();
    } catch (error) {
      console.error("Error deleting document type:", error);
    }
  };

  const cancelEdit = () => {
    setFormState((prev) => ({
      ...prev,
      editType: {
        data: null,
        name: "",
        slug: "",
        isTemplate: false,
        orderTypeIds: [],
      },
    }));
  };

  const toggleTemplate = async (type: any) => {
    try {
      await updateData(`${endpoints.documentTypes}/${type._id}`, {
        isTemplate: !type.isTemplate,
      });
      onRefresh();
    } catch (error) {
      console.error("Error toggling template:", error);
    }
  };

  const addNewTypeInline = async () => {
    if (!formState.newType.name.trim()) return;

    try {
      const payload: any = {
        name: formState.newType.name.trim(),
        slug: formState.newType.slug.trim(),
        isTemplate: formState.newType.isTemplate,
        orderTypeIds: formState.newType.orderTypeIds,
      };

      await postData(endpoints.documentTypes, payload);
      onRefresh();
      setFormState((prev) => ({
        ...prev,
        newType: {
          name: "",
          slug: "",
          isTemplate: false,
          orderTypeIds: [],
        },
        showAddForm: false,
      }));
    } catch (error) {
      console.error("Error adding document type:", error);
    }
  };

  const cancelAddInline = () => {
    setFormState((prev) => ({
      ...prev,
      newType: {
        name: "",
        slug: "",
        isTemplate: false,
        orderTypeIds: [],
      },
      showAddForm: false,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{ fontSize: "1.5rem", fontWeight: "bold" }}
        >
          {Vocabulary.defaultManagement}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mt: 1, fontSize: "1rem" }}
        >
          {Vocabulary.add}, {Vocabulary.edit} sau {Vocabulary.delete} tipurile
          de documente.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Existing Document Types */}
          {documentTypes.map((type) => (
            <Card
              key={type._id}
              variant="outlined"
              sx={{ bgcolor: "background.paper" }}
            >
              <CardContent sx={{ p: 2 }}>
                {formState.editType.data?._id === type._id ? (
                  // Edit Mode
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          label={Vocabulary.name}
                          value={formState.editType.name}
                          onChange={(e) =>
                            setFormState((prev) => ({
                              ...prev,
                              editType: {
                                ...prev.editType,
                                name: e.target.value,
                              },
                            }))
                          }
                          size="small"
                          fullWidth
                          autoFocus
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "1.1rem",
                            },
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdateType();
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                          {Vocabulary.template}
                        </Typography>
                        <Switch
                          checked={formState.editType.isTemplate}
                          onChange={(e) =>
                            setFormState((prev) => ({
                              ...prev,
                              editType: {
                                ...prev.editType,
                                isTemplate: e.target.checked,
                              },
                            }))
                          }
                          size="small"
                        />
                      </Box>
                    </Box>

                    <Autocomplete
                      multiple
                      fullWidth
                      size="small"
                      options={orderTypes}
                      value={orderTypes.filter((option: any) =>
                        formState.editType.orderTypeIds.includes(option._id)
                      )}
                      getOptionLabel={(option: any) => option?.name || ""}
                      isOptionEqualToValue={(option: any, value: any) =>
                        option?._id === value?._id
                      }
                      onChange={(_, values) =>
                        setFormState((prev) => ({
                          ...prev,
                          editType: {
                            ...prev.editType,
                            orderTypeIds: values.map((v: any) => v._id),
                          },
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label={Vocabulary.productType}
                        />
                      )}
                    />

                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={handleUpdateType}
                        color="primary"
                      >
                        <Check />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={cancelEdit}
                        color="default"
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  // View Mode
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "1.1rem", fontWeight: "medium" }}
                      >
                        {type.name}
                      </Typography>
                      {type.isTemplate && (
                        <Box
                          sx={{
                            bgcolor: "primary.light",
                            color: "primary.contrastText",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: "0.8rem",
                          }}
                        >
                          {Vocabulary.template}
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                        {Vocabulary.template}
                      </Typography>
                      <Switch
                        checked={type.isTemplate || false}
                        onChange={() => toggleTemplate(type)}
                        size="small"
                      />
                    </Box>
                    {!type.isTemplate && (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title={Vocabulary.edit}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditType(type)}
                            color="default"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={Vocabulary.delete}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteType(type._id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Add New Type Form */}
          {formState.showAddForm ? (
            <Card
              variant="outlined"
              sx={{
                border: "2px dashed",
                borderColor: "primary.main",
                bgcolor: "rgba(25, 118, 210, 0.04)",
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label={Vocabulary.name}
                    value={formState.newType.name}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        newType: { ...prev.newType, name: e.target.value },
                      }))
                    }
                    size="small"
                    fullWidth
                    autoFocus
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "1.1rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "1rem",
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addNewTypeInline();
                      if (e.key === "Escape") cancelAddInline();
                    }}
                  />

                  <TextField
                    label="Slug"
                    value={formState.newType.slug}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        newType: { ...prev.newType, slug: e.target.value },
                      }))
                    }
                    size="small"
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "1.1rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "1rem",
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addNewTypeInline();
                      if (e.key === "Escape") cancelAddInline();
                    }}
                  />

                  <Autocomplete
                    multiple
                    fullWidth
                    size="small"
                    options={orderTypes}
                    value={orderTypes.filter((option: any) =>
                      formState.newType.orderTypeIds.includes(option._id)
                    )}
                    getOptionLabel={(option: any) => option?.name || ""}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?._id === value?._id
                    }
                    onChange={(_, values) =>
                      setFormState((prev) => ({
                        ...prev,
                        newType: {
                          ...prev.newType,
                          orderTypeIds: values.map((v: any) => v._id),
                        },
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={Vocabulary.productType}
                      />
                    )}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.newType.isTemplate}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            newType: {
                              ...prev.newType,
                              isTemplate: e.target.checked,
                            },
                          }))
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "1rem" }}>
                        {Vocabulary.template}
                      </Typography>
                    }
                  />

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={addNewTypeInline}
                      disabled={!formState.newType.name.trim()}
                      startIcon={<AddIcon />}
                      sx={{ fontSize: "1rem" }}
                    >
                      {Vocabulary.add}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={cancelAddInline}
                      sx={{ fontSize: "1rem" }}
                    >
                      {Vocabulary.cancel}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Button
              variant="outlined"
              onClick={() =>
                setFormState((prev) => ({ ...prev, showAddForm: true }))
              }
              sx={{
                height: 40,
                border: "2px dashed",
                borderColor: "primary.main",
                fontSize: "1rem",
              }}
              startIcon={<AddIcon />}
            >
              {Vocabulary.add} {Vocabulary.addNewDocumentType}
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ fontSize: "1rem" }}>
          {Vocabulary.cancel}
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ fontSize: "1rem" }}>
          {Vocabulary.save} {Vocabulary.unsavedChangesTitleModal}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
